/**
 * Batch #14.195 — Page admin "Suivi des communications".
 *
 * Liste tous les envois de notifications (multi-canal) avec leur statut :
 * - Envoyé / Ouvert / Cliqué / Action complétée / Échec
 * - Filtres par statut, type d'action (rent_increase, bail_tal, chat, etc.), période
 * - Bouton "Relance" pour forcer un rappel
 * - Bouton "Run reminders" pour relancer en masse les notifs > 2j non vues
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import {
  Mail, MailOpen, MousePointerClick, CheckCircle2, AlertTriangle,
  RefreshCw, Send, Loader2, Smartphone, MessageSquare,
  Plus, Trash2, X, Save, Megaphone, Bell, Ban, RotateCcw,
} from "lucide-react";
import ArchiveButton from "@/components/admin/ArchiveButton";

const STATUS_DEF = {
  sent:    { label: "Envoyée", icon: Mail, color: "bg-blue-100 text-blue-700 border-blue-200" },
  opened:  { label: "Ouverte", icon: MailOpen, color: "bg-amber-100 text-amber-700 border-amber-200" },
  clicked: { label: "Cliquée", icon: MousePointerClick, color: "bg-violet-100 text-violet-700 border-violet-200" },
  read:    { label: "Lue", icon: CheckCircle2, color: "bg-cyan-100 text-cyan-700 border-cyan-200" },
  received:{ label: "Reçue", icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  signed:  { label: "Signée", icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  reminded:{ label: "Relancée", icon: AlertTriangle, color: "bg-orange-100 text-orange-700 border-orange-200" },
  failed:  { label: "Échec", icon: AlertTriangle, color: "bg-rose-100 text-rose-700 border-rose-200" },
  cancelled:{ label: "Suivi annulé", icon: Ban, color: "bg-slate-100 text-slate-600 border-slate-200" },
};

const TYPE_LABELS = {
  rent_increase: "Avis de renouvellement",
  bail_tal: "Bail TAL",
  chat: "Chat",
  wo: "Suivi de travaux",
  rl31: "Relevé 31",
  credit: "Enquête crédit",
  invoice: "Facture",
  comm_sent: "Document envoyé",
  comm_received: "Dépôt reçu",
  comm_to_sign: "Doc à signer",
  manual_relance: "Relance manuelle",
  other: "Autre",
};

export default function CommunicationsLogPage() {
  const [data, setData] = useState({ items: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [busyRelance, setBusyRelance] = useState(null);
  const [busyRunReminders, setBusyRunReminders] = useState(false);
  // Mode « Bloquées > 2 j » : la liste exacte derrière la pastille Centre de communications
  const [stuckOnly, setStuckOnly] = useState(false);
  const [busyFollowup, setBusyFollowup] = useState(null);
  // Batch #14.197 — Nouvelle relance manuelle
  const [showRelanceModal, setShowRelanceModal] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days), limit: "200" });
      if (statusFilter) params.set("status", statusFilter);
      if (typeFilter) params.set("related_type", typeFilter);
      const r = await api.get(`/admin/notifications/communications?${params}`);
      if (stuckOnly) {
        const st = await api.get("/admin/notifications/stuck");
        setData({ items: st.data.items || [], stats: { ...r.data.stats, stuck_2d: st.data.count } });
      } else {
        setData(r.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [days, statusFilter, typeFilter, stuckOnly]);

  useEffect(() => { load(); }, [load]);

  const relance = async (id) => {
    if (!window.confirm("Envoyer un rappel manuel pour cette notification ?")) return;
    setBusyRelance(id);
    try {
      const r = await api.post(`/admin/notifications/relance/${id}`);
      toast.success(`Rappel envoyé via : ${r.data.sent_via.join(", ") || "(rien)"}`);
      refreshBadges();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusyRelance(null);
    }
  };

  const refreshBadges = () => window.dispatchEvent(new CustomEvent("badges:refresh"));

  const cancelFollowup = async (id) => {
    if (!window.confirm("Annuler le suivi de cette communication ?\n\nElle sort de la pastille et des relances automatiques (le client ne sera plus relancé).")) return;
    setBusyFollowup(id);
    try {
      await api.post(`/admin/notifications/${id}/cancel-followup`);
      toast.success("Suivi annulé", { duration: 6000, action: { label: "Rétablir", onClick: () => restoreFollowup(id) } });
      if (stuckOnly) setDays(0);
      refreshBadges();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusyFollowup(null);
    }
  };

  const restoreFollowup = async (id) => {
    setBusyFollowup(id);
    try {
      await api.post(`/admin/notifications/${id}/restore-followup`);
      toast.success("Suivi repris");
      refreshBadges();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusyFollowup(null);
    }
  };

  const runReminders = async () => {
    if (!window.confirm("Lancer la passe de relances automatiques pour TOUTES les notifs > 2 jours non vues ?")) return;
    setBusyRunReminders(true);
    try {
      const r = await api.post("/admin/notifications/run-reminders?days=2");
      toast.success(`${r.data.relanced} relances envoyées sur ${r.data.candidates} candidates`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusyRunReminders(false);
    }
  };

  const stats = data.stats || {};

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="communications-log-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <MessageSquare className="w-6 h-6 text-cyan-600" /> Suivi des communications
        </h1>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setShowRelanceModal(true)}
            className="text-xs px-3 py-2 rounded-lg bg-cyan-600 text-white font-semibold hover:bg-cyan-700 inline-flex items-center gap-1.5"
            data-testid="comm-new-relance"
          >
            <Megaphone className="w-3.5 h-3.5" /> Nouvelle relance
          </button>
          <button
            onClick={() => setShowTemplatesModal(true)}
            className="text-xs px-3 py-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1.5"
            data-testid="comm-templates"
          >
            <Bell className="w-3.5 h-3.5" /> Templates
          </button>
          <button
            onClick={runReminders}
            disabled={busyRunReminders}
            className="text-xs px-3 py-2 rounded-lg bg-orange-600 text-white font-semibold hover:bg-orange-700 disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="comm-run-reminders"
          >
            {busyRunReminders ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Lancer les relances (2j+)
          </button>
          <button onClick={load} className="text-xs px-3 py-2 rounded-lg border border-slate-200 inline-flex items-center gap-1.5" data-testid="comm-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Rafraîchir
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
        <StatCard label="Total" value={stats.total ?? 0} tone="slate" />
        <StatCard label="Ouvertes" value={stats.opened ?? 0} tone="amber" />
        <StatCard label="Cliquées" value={stats.clicked ?? 0} tone="violet" />
        <StatCard label="Terminées" value={stats.completed ?? 0} tone="emerald" />
        <StatCard label="Bloquées > 2j" value={stats.stuck_2d ?? 0} tone="rose" alert
          active={stuckOnly} onClick={() => setStuckOnly((v) => !v)} testId="comm-stat-stuck" />
      </div>
      {stuckOnly && (
        <div className="mb-3 text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded-lg px-3 py-2 flex items-center justify-between gap-2" data-testid="comm-stuck-banner">
          <span>Suivis sans réponse depuis 2 jours — c'est la liste derrière la pastille <b>Centre de communications</b>. « Relancer » ou « Annuler le suivi » si le client n'agira pas.</span>
          <button onClick={() => setStuckOnly(false)} className="underline whitespace-nowrap" data-testid="comm-stuck-exit">Voir tout</button>
        </div>
      )}

      {/* Filtres */}
      {!stuckOnly && <div className="flex flex-wrap items-center gap-2 mb-3">
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="text-xs px-2 py-1 border border-slate-200 rounded" data-testid="comm-filter-days">
          <option value={1}>1 jour</option>
          <option value={7}>7 jours</option>
          <option value={30}>30 jours</option>
          <option value={90}>90 jours</option>
          <option value={0}>Toutes périodes</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="text-xs px-2 py-1 border border-slate-200 rounded" data-testid="comm-filter-status">
          <option value="">Tous statuts</option>
          {Object.entries(STATUS_DEF).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="text-xs px-2 py-1 border border-slate-200 rounded" data-testid="comm-filter-type">
          <option value="">Toutes actions</option>
          {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>}

      {/* Liste */}
      {loading ? (
        <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /></div>
      ) : data.items.length === 0 ? (
        <div className="bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl p-10 text-center text-slate-400">
          {stuckOnly ? "Aucun suivi bloqué — la pastille est à zéro." : "Aucune communication récente"}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr className="text-left text-[10px] uppercase font-bold text-slate-500">
                <th className="px-3 py-2">Quand</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Destinataire</th>
                <th className="px-3 py-2">Canaux</th>
                <th className="px-3 py-2">Statut</th>
                <th className="px-3 py-2">Détail</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => (
                <Row key={it.id} item={it} onRelance={relance} busy={busyRelance === it.id}
                  onCancelFollowup={cancelFollowup} onRestoreFollowup={restoreFollowup} busyFollowup={busyFollowup === it.id} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Batch #14.197 — Modales */}
      {showRelanceModal && (
        <RelanceModal onClose={() => setShowRelanceModal(false)} onSent={() => { setShowRelanceModal(false); load(); }} />
      )}
      {showTemplatesModal && (
        <TemplatesModal onClose={() => setShowTemplatesModal(false)} />
      )}
    </div>
  );
}

function Row({ item, onRelance, busy, onCancelFollowup, onRestoreFollowup, busyFollowup }) {
  const cancelled = !!item.followup_cancelled_at || item.status === "cancelled";
  const conf = (cancelled ? STATUS_DEF.cancelled : STATUS_DEF[item.status]) || STATUS_DEF.sent;
  const Icon = conf.icon;
  const isStuck = !cancelled && !item.email_opened_at && !item.magic_link_clicked_at && !item.final_action_at && !item.resolved_externally;
  const ageDays = item.sent_at ? Math.floor((Date.now() - new Date(item.sent_at).getTime()) / 86400000) : 0;
  const stuckTooLong = isStuck && ageDays >= 2;
  return (
    <tr className={`border-t border-slate-100 hover:bg-slate-50/50 ${stuckTooLong ? "bg-rose-50/40" : ""}`} data-testid={`comm-row-${item.id}`}>
      <td className="px-3 py-2 align-top">
        <div className="text-slate-700">{item.sent_at ? new Date(item.sent_at).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" }) : "—"}</div>
        {ageDays > 0 && <div className="text-[10px] text-slate-400">il y a {ageDays}j</div>}
      </td>
      <td className="px-3 py-2 align-top">
        <div className="font-semibold text-slate-700">{TYPE_LABELS[item.related_type] || item.related_type}</div>
        {item.purpose && <div className="text-[10px] text-slate-400">{item.purpose}</div>}
      </td>
      <td className="px-3 py-2 align-top">
        <div className="font-semibold text-slate-800">{item.recipient_name || "—"}</div>
        {item.recipient_email && <div className="text-[10px] text-slate-500 truncate max-w-[200px]">{item.recipient_email}</div>}
        {item.recipient_phone && <div className="text-[10px] text-slate-500 font-mono">{item.recipient_phone}</div>}
      </td>
      <td className="px-3 py-2 align-top">
        <div className="flex gap-1 flex-wrap">
          {(item.channels_succeeded || []).map((c) => (
            <span key={c} className="text-[10px] bg-emerald-100 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5">
              {c === "sms" ? <Smartphone className="w-2.5 h-2.5" /> : c === "email" ? <Mail className="w-2.5 h-2.5" /> : null}
              {c}
            </span>
          ))}
          {(item.channels_failed || []).map((c) => (
            <span key={c} className="text-[10px] bg-rose-100 text-rose-700 border border-rose-200 px-1.5 py-0.5 rounded line-through" title="Échec">
              {c}
            </span>
          ))}
        </div>
        {item.sms_dlr_status && (
          <div className="text-[9px] mt-0.5">
            <span className="font-mono">DLR : {item.sms_dlr_status}</span>
          </div>
        )}
      </td>
      <td className="px-3 py-2 align-top">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded font-semibold text-[10px] border ${conf.color}`}>
          <Icon className="w-3 h-3" /> {conf.label}
        </span>
        {stuckTooLong && (
          <div className="text-[9px] text-rose-600 font-bold mt-0.5 inline-flex items-center gap-0.5">
            <AlertTriangle className="w-2.5 h-2.5" /> Inactif {ageDays}j
          </div>
        )}
      </td>
      <td className="px-3 py-2 align-top text-[10px] text-slate-600 space-y-0.5">
        {item.email_opened_at && (
          <div className="text-amber-700">👁️ Ouvert : {fmtRelative(item.email_opened_at)}</div>
        )}
        {item.magic_link_clicked_at && (
          <div className="text-violet-700">🔗 Cliqué : {fmtRelative(item.magic_link_clicked_at)}</div>
        )}
        {item.portal_visited_after && (
          <div className="text-cyan-700">🚪 Portail visité après</div>
        )}
        {item.invoice_paid && (
          <div className="text-emerald-700 font-semibold">💰 Facture payée</div>
        )}
        {item.final_action_at && (
          <div className="text-emerald-700">✅ Action : {fmtRelative(item.final_action_at)}</div>
        )}
        {item.reminder_sent_at && (
          <div className="text-orange-600">📤 Relancée : {fmtRelative(item.reminder_sent_at)}</div>
        )}
        {cancelled && (
          <div className="text-slate-500">🚫 Suivi annulé : {fmtRelative(item.followup_cancelled_at)}</div>
        )}
      </td>
      <td className="px-3 py-2 align-top">
        <div className="inline-flex items-center gap-1">
          {cancelled ? (
            <button
              onClick={() => onRestoreFollowup(item.id)}
              disabled={busyFollowup}
              className="text-[10px] px-2 py-1 rounded bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200 disabled:opacity-50 inline-flex items-center gap-0.5"
              data-testid={`comm-restore-followup-${item.id}`}
            >
              {busyFollowup ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
              Reprendre le suivi
            </button>
          ) : !item.final_action_at && (
            <button
              onClick={() => onCancelFollowup(item.id)}
              disabled={busyFollowup}
              className="text-[10px] px-2 py-1 rounded bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200 disabled:opacity-50 inline-flex items-center gap-0.5"
              title="Le client n'agira pas : retirer de la pastille et des relances"
              data-testid={`comm-cancel-followup-${item.id}`}
            >
              {busyFollowup ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ban className="w-3 h-3" />}
              Annuler le suivi
            </button>
          )}
          {!cancelled && !item.final_action_at && (
            <button
              onClick={() => onRelance(item.id)}
              disabled={busy}
              className="text-[10px] px-2 py-1 rounded bg-orange-100 text-orange-700 border border-orange-200 hover:bg-orange-200 disabled:opacity-50 inline-flex items-center gap-0.5"
              data-testid={`comm-relance-${item.id}`}
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              Relancer
            </button>
          )}
          <ArchiveButton itemType="notif_log" itemId={item.id} itemLabel="cette ligne de tracking" size="xs" />
        </div>
      </td>
    </tr>
  );
}

function StatCard({ label, value, tone, alert, active, onClick, testId }) {
  const tones = {
    slate: "bg-slate-100 border-slate-200 text-slate-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    violet: "bg-violet-50 border-violet-200 text-violet-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
  };
  const Tag = onClick ? "button" : "div";
  return (
    <Tag onClick={onClick} data-testid={testId}
      className={`rounded-lg border p-2.5 text-left ${tones[tone] || tones.slate} ${alert && value > 0 ? "ring-2 ring-rose-300" : ""} ${active ? "ring-2 ring-rose-500 shadow" : ""} ${onClick ? "hover:shadow cursor-pointer" : ""}`}>
      <div className="text-[10px] uppercase font-bold opacity-70">{label}{onClick && <span className="ml-1 normal-case font-normal">— cliquer pour {active ? "tout voir" : "voir la liste"}</span>}</div>
      <div className="text-2xl font-bold mt-0.5 tabular-nums">{value}</div>
    </Tag>
  );
}

function fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / 60000); // min
    if (diff < 1) return "à l'instant";
    if (diff < 60) return `il y a ${diff}m`;
    if (diff < 1440) return `il y a ${Math.floor(diff/60)}h`;
    return d.toLocaleDateString("fr-CA");
  } catch {
    return iso.slice(0, 16);
  }
}

// ════════════════════════════════════════════════════════════════════════════
// Batch #14.197 — Modale Nouvelle relance (1 client, message personnalisable)
// ════════════════════════════════════════════════════════════════════════════
function RelanceModal({ onClose, onSent }) {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [forceEmail, setForceEmail] = useState(true);
  const [forceSms, setForceSms] = useState(false);
  const [forcePush, setForcePush] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/admin/clients-portal").then((r) => setClients(r.data?.items || [])).catch((e) => { console.error("[RelanceModal] load clients failed:", e); });
    api.get("/admin/relance-templates").then((r) => setTemplates(r.data?.items || [])).catch((e) => { console.error("[RelanceModal] load templates failed:", e); });
  }, []);

  const filtered = clients.filter((c) =>
    (c.nom || "").toLowerCase().includes(search.toLowerCase()) ||
    (c.email || "").toLowerCase().includes(search.toLowerCase()),
  ).slice(0, 30);

  const applyTemplate = (tpl) => {
    setSubject(tpl.subject);
    setBody(tpl.body);
    toast.success(`Template « ${tpl.name} » appliqué`);
  };

  const send = async () => {
    if (!selectedClient) return toast.error("Sélectionnez un client");
    if (!subject.trim() || !body.trim()) return toast.error("Sujet et message requis");
    if (!forceEmail && !forceSms && !forcePush) return toast.error("Au moins un canal requis");
    setSending(true);
    try {
      const r = await api.post("/admin/notifications/send-generic-relance", {
        client_id: selectedClient.id,
        subject,
        body,
        force_email: forceEmail,
        force_sms: forceSms,
        force_push: forcePush,
      });
      const okChannels = r.data.channels_ok || r.data.channels_attempted || [];
      const warnings = r.data.warnings || [];
      if (warnings.length > 0) {
        toast.warning(
          `Envoyé via : ${okChannels.join(", ") || "(rien)"}\n⚠ ${warnings.join(" / ")}`,
          { duration: 8000 },
        );
      } else {
        toast.success(`Relance envoyée via : ${okChannels.join(", ")}`);
      }
      onSent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi échoué");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full p-5 max-h-[90vh] overflow-y-auto" data-testid="relance-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-900 flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-cyan-600" /> Nouvelle relance générique
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
        </div>

        {/* Sélection client */}
        <div className="mb-4">
          <label className="text-xs font-bold text-slate-700 uppercase">Client destinataire *</label>
          {selectedClient ? (
            <div className="mt-1 flex items-center justify-between gap-2 bg-cyan-50 border border-cyan-300 rounded p-2">
              <div>
                <div className="font-semibold text-sm text-slate-800">{selectedClient.nom}</div>
                <div className="text-[11px] text-slate-500">{selectedClient.email || "—"} · {selectedClient.phone || "—"}</div>
              </div>
              <button onClick={() => setSelectedClient(null)} className="text-xs text-slate-500 hover:text-slate-800">Changer</button>
            </div>
          ) : (
            <>
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher par nom ou email..."
                className="w-full mt-1 px-3 py-2 text-sm border border-slate-200 rounded-lg"
                data-testid="relance-client-search" />
              <div className="mt-1 max-h-40 overflow-y-auto border border-slate-200 rounded">
                {filtered.length === 0 ? (
                  <div className="p-3 text-center text-xs text-slate-400">Aucun client trouvé</div>
                ) : filtered.map((c) => (
                  <button key={c.id} onClick={() => setSelectedClient(c)}
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-cyan-50 border-b border-slate-100"
                    data-testid={`relance-client-pick-${c.id}`}>
                    <div className="font-semibold text-slate-800">{c.nom}</div>
                    <div className="text-[10px] text-slate-500">{c.email || "—"} · {c.phone || "—"}</div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Templates rapides */}
        {templates.length > 0 && (
          <div className="mb-4">
            <label className="text-xs font-bold text-slate-700 uppercase">Templates rapides</label>
            <div className="flex flex-wrap gap-1 mt-1">
              {templates.map((t) => (
                <button key={t.id} onClick={() => applyTemplate(t)}
                  className="text-[11px] px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 hover:border-cyan-400"
                  data-testid={`relance-template-${t.id}`}>
                  {t.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Message */}
        <div className="space-y-2">
          <div>
            <label className="text-xs font-bold text-slate-700 uppercase">Sujet *</label>
            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
              placeholder="Ex: Rappel — votre déclaration de revenus"
              className="w-full mt-1 px-3 py-2 text-sm border border-slate-200 rounded-lg"
              data-testid="relance-subject" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-700 uppercase">Message *</label>
            <textarea rows={6} value={body} onChange={(e) => setBody(e.target.value)}
              placeholder="Bonjour {first_name},

Nous attendons toujours...

Cordialement,
L'équipe DG Comptabilité"
              className="w-full mt-1 px-3 py-2 text-sm border border-slate-200 rounded-lg font-mono"
              data-testid="relance-body" />
            <p className="text-[10px] text-slate-400 mt-0.5">Variables disponibles : <code>{"{first_name}"}</code>, <code>{"{last_name}"}</code>, <code>{"{full_name}"}</code></p>
          </div>
        </div>

        {/* Canaux forcés */}
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded p-3">
          <div className="text-xs font-bold text-amber-800 uppercase mb-2">Canaux d'envoi (forçage — ignore les préférences client)</div>
          <div className="flex flex-wrap gap-3">
            <label className="flex items-center gap-1.5 text-sm">
              <input type="checkbox" checked={forceEmail} onChange={(e) => setForceEmail(e.target.checked)} data-testid="relance-force-email" />
              <Mail className="w-3.5 h-3.5 text-blue-600" /> Email
              {forceEmail && selectedClient && !selectedClient.email && (
                <span className="text-[10px] text-rose-600 font-semibold">⚠ client sans email</span>
              )}
            </label>
            <label className="flex items-center gap-1.5 text-sm">
              <input type="checkbox" checked={forceSms} onChange={(e) => setForceSms(e.target.checked)} data-testid="relance-force-sms" />
              <Smartphone className="w-3.5 h-3.5 text-emerald-600" /> SMS
              {forceSms && selectedClient && !selectedClient.phone && (
                <span className="text-[10px] text-rose-600 font-semibold">⚠ client sans téléphone</span>
              )}
            </label>
            <label className="flex items-center gap-1.5 text-sm">
              <input type="checkbox" checked={forcePush} onChange={(e) => setForcePush(e.target.checked)} data-testid="relance-force-push" />
              <Bell className="w-3.5 h-3.5 text-violet-600" /> Push
            </label>
          </div>
          {forceSms && selectedClient && !selectedClient.phone && (
            <div className="mt-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
              <b>Le SMS ne pourra pas être envoyé</b> : la fiche de {selectedClient.nom || "ce client"} n'a pas de numéro de téléphone.
              Allez dans la fiche client pour en ajouter un (format <code>+1418XXXXXXX</code>).
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
          <button onClick={onClose} className="text-xs px-3 py-1.5 rounded border">Annuler</button>
          <button onClick={send} disabled={sending || !selectedClient}
            className="text-xs px-4 py-1.5 rounded bg-cyan-600 text-white font-bold disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="relance-send">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Envoyer la relance
          </button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Batch #14.197 — Modale Templates de relance (CRUD)
// ════════════════════════════════════════════════════════════════════════════
function TemplatesModal({ onClose }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | {id?, name, subject, body}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/relance-templates");
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error("Erreur de chargement");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => setEditing({ name: "", subject: "", body: "" });

  const save = async () => {
    if (!editing.name?.trim() || !editing.subject?.trim() || !editing.body?.trim()) {
      return toast.error("Nom, sujet et message requis");
    }
    setBusy(true);
    try {
      const payload = { name: editing.name, subject: editing.subject, body: editing.body };
      if (editing.id) {
        await api.put(`/admin/relance-templates/${editing.id}`, payload);
        toast.success("Template mis à jour");
      } else {
        await api.post("/admin/relance-templates", payload);
        toast.success("Template créé");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce template ?")) return;
    try {
      await api.delete(`/admin/relance-templates/${id}`);
      toast.success("Supprimé");
      load();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-5 max-h-[90vh] overflow-y-auto" data-testid="templates-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-900 flex items-center gap-2">
            <Bell className="w-5 h-5 text-cyan-600" /> Templates de relance
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
        </div>

        {editing ? (
          <div className="space-y-2 bg-cyan-50 border border-cyan-200 rounded p-3">
            <input type="text" placeholder="Nom du template (ex: Rappel documents fiscaux)"
              value={editing.name} onChange={(e) => setEditing({...editing, name: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded"
              data-testid="template-name" />
            <input type="text" placeholder="Sujet"
              value={editing.subject} onChange={(e) => setEditing({...editing, subject: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded"
              data-testid="template-subject" />
            <textarea rows={5} placeholder="Message (utilisez {first_name}, {last_name}, {full_name})"
              value={editing.body} onChange={(e) => setEditing({...editing, body: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded font-mono"
              data-testid="template-body" />
            <div className="flex gap-1 justify-end">
              <button onClick={() => setEditing(null)} className="text-xs px-2 py-1 rounded border">Annuler</button>
              <button onClick={save} disabled={busy} className="text-xs px-3 py-1 rounded bg-cyan-600 text-white font-bold inline-flex items-center gap-1 disabled:opacity-50" data-testid="template-save">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                Enregistrer
              </button>
            </div>
          </div>
        ) : (
          <>
            <button onClick={startNew} className="text-xs px-3 py-1.5 rounded bg-cyan-600 text-white font-bold inline-flex items-center gap-1 mb-3" data-testid="template-new">
              <Plus className="w-3 h-3" /> Nouveau template
            </button>
            {items.length === 0 ? (
              <div className="bg-slate-50 border-2 border-dashed border-slate-200 rounded p-6 text-center text-sm text-slate-400">
                Aucun template — créez-en un pour gagner du temps lors des relances
              </div>
            ) : (
              <div className="space-y-1.5">
                {items.map((t) => (
                  <div key={t.id} className="border border-slate-200 rounded p-2 hover:border-cyan-300" data-testid={`template-item-${t.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-slate-800">{t.name}</div>
                        <div className="text-[11px] text-slate-500 italic">{t.subject}</div>
                        <div className="text-[10px] text-slate-400 mt-1 line-clamp-2">{t.body}</div>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => setEditing(t)} className="text-[10px] px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50">Modifier</button>
                        <button onClick={() => remove(t.id)} className="text-[10px] px-2 py-0.5 rounded border border-rose-200 text-rose-600 hover:bg-rose-50" data-testid={`template-delete-${t.id}`}>
                          <Trash2 className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <div className="flex justify-end mt-4 pt-3 border-t">
          <button onClick={onClose} className="text-xs px-3 py-1.5 rounded border">Fermer</button>
        </div>
      </div>
    </div>
  );
}
